import { useEffect, useRef, type RefObject } from 'react';
import { findNodeHandle, Platform } from 'react-native';

// 클릭으로 인정할 최대 이동량(px). 이보다 크게 움직이면 드래그로 보고, 뒤따르는 click 을 막아
// 배너 딥링크가 실수로 열리지 않게 한다.
const DRAG_THRESHOLD = 5;

/**
 * 터치 제스처의 주축(가로/세로)을 확정하는 이동량(px).
 *
 * 확정 전에는 아무것도 하지 않는다. 세로로 확정되면 이 훅은 손을 떼고 브라우저가 페이지를
 * 스크롤하게 둔다(`touch-action: pan-y`). 축을 잠그지 않으면 세로로 넘기는 동안에도 가로
 * 성분만큼 배너가 밀려 어긋난다.
 */
const AXIS_LOCK_THRESHOLD = 6;

/**
 * 이 속도(px/ms) 이상이면 이동 거리와 무관하게 다음 장으로 넘긴다.
 *
 * 브라우저 관성을 쓰지 않으므로(아래 touch-action 주석 참고) "짧고 빠르게 튕기기"를 거리로만
 * 판정하면 절반을 못 넘겨 제자리로 돌아온다. 네이티브 페이징의 체감을 맞추기 위한 값이다.
 */
const FLICK_VELOCITY_THRESHOLD = 0.35;

/** 속도 표본이 이보다 오래됐으면 손가락이 멈춘 것으로 보고 flick 으로 인정하지 않는다. */
const VELOCITY_SAMPLE_MAX_AGE_MS = 120;

type WebDragToScrollOptions = {
  /**
   * 한 페이지 폭(px). 주면 **한 번의 드래그로 최대 한 페이지까지만** 움직인다.
   *
   * 페이지 단위(배너/캐러셀) 스크롤에 필요하다. 마우스 드래그는 `scrollLeft` 를 손 움직임에
   * 그대로 따라가게 하므로, 길게 끌면 여러 장이 한 번에 넘어간다. 시작 위치 ±1 페이지로
   * 제한하면 드래그 도중에도 한 장 이상 벗어나지 않는다.
   *
   * 자유 스크롤(칩 리스트 등)은 넘기지 않는다.
   */
  pageWidth?: number;
  /**
   * 가로 휠/트랙패드 스크롤을 막는다(기본 false).
   *
   * 페이징 배너처럼 "한 번에 한 장"이 계약인 곳에서 쓴다. 휠은 연속 delta 이벤트라
   * 제스처 시작점이 없어 한 장 제한을 걸 수 없고(스크롤 도중 계속 되돌리는 저항이 된다),
   * 배너가 트랙패드 제스처를 먹어 페이지 수직 스크롤을 방해하는 문제도 있다.
   *
   * ⚠️ 세로 휠은 그대로 통과시킨다 — 배너 위에서 페이지가 스크롤되지 않으면 더 큰 문제다.
   */
  blockHorizontalWheel?: boolean;
  /** 드래그가 실제로 시작된 시점(임계값 초과). 자동 롤링 정지 등에 쓴다. */
  onDragStart?: () => void;
  /** 드래그가 끝난 시점(mouseup). 드래그 없이 클릭만 한 경우엔 호출하지 않는다. */
  onDragEnd?: () => void;
};

/**
 * web 전용 drag-to-scroll. **마우스와 터치 모두** 팬을 직접 구현한다.
 *
 * ## 마우스
 * 브라우저는 overflow 스크롤 컨테이너를 마우스 click-drag 로 스크롤하지 않는다(휠/트랙패드만).
 * RN Web ScrollView 도 결국 overflow div 라 동일. 그래서 스크롤 노드에 mouse 이벤트를 붙여
 * `scrollLeft` 를 직접 조작한다.
 *
 * ## 터치 (`pageWidth` 를 준 페이징 모드에서만)
 * 브라우저는 터치 팬을 알아서 해주지만 **한 제스처가 한 페이지를 넘지 못하게 저지하는 수단이
 * 없다.** CSS scroll-snap 은 손을 뗀 뒤 정렬만 하고(`scroll-snap-stop: always` 도 관성 구간에만
 * 관여한다), 드래그 자체는 얼마든지 허용해서 한 번에 여러 장이 넘어간다. 네이티브
 * `pagingEnabled` 에 대응하는 게 웹에는 없다.
 *
 * 그래서 스크롤포트에 `touch-action: pan-y` 를 주어 **브라우저의 가로 팬을 끄고**(세로 페이지
 * 스크롤은 그대로 통과) 가로 이동을 마우스와 같은 경로로 처리한다. 관성이 아예 생기지 않으므로
 * "JS 스냅이 브라우저 관성과 싸운다" 는 문제도 구조적으로 없어진다. 대신 관성을 잃은 만큼
 * 손을 뗄 때 **속도 판정**으로 다음 장을 결정한다(FLICK_VELOCITY_THRESHOLD).
 *
 * `pageWidth` 가 없는 자유 스크롤(칩 리스트 등)은 터치를 건드리지 않는다 — 페이지 개념이 없어
 * 제한할 것도 없고, 브라우저 관성이 그대로가 더 자연스럽다.
 *
 * ⚠️ RN Web 의 `onTouchStart` / `onScrollBeginDrag` 는 **마우스에선 발화하지 않고**, 터치에서도
 * 가로 팬을 우리가 가져가면 `onScrollBeginDrag` 가 발화하지 않는다. 드래그 시작·종료를 알아야
 * 하는 소비 측(자동 롤링 정지 등)은 여기서 주는 `onDragStart` / `onDragEnd` 만 쓰면 된다.
 */
export const useWebDragToScroll = <
  T extends {
    getScrollableNode?: () => unknown;
    scrollTo?: (opts: { x?: number; animated?: boolean }) => void;
  },
>(
  scrollRef: RefObject<T | null>,
  options: WebDragToScrollOptions = {},
) => {
  // 리스너를 매 렌더 재부착하지 않도록 옵션은 ref 로 읽는다(콜백 identity 가 바뀌어도 무해).
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const inst = scrollRef.current;
    if (!inst) return;
    // getScrollableNode() 가 노드를 안 주면 findNodeHandle 로 폴백(RN Web 은 DOM 노드 반환).
    const node = (inst.getScrollableNode?.() ??
      findNodeHandle(
        inst as unknown as Parameters<typeof findNodeHandle>[0],
      )) as HTMLElement | null;
    if (!node) return;

    let isDown = false;
    let moved = false;
    let startX = 0;
    let startScrollLeft = 0;

    // 터치 상태. 마우스와 제스처가 겹치지 않으므로 startScrollLeft / moved 는 공유한다.
    let touchId: number | null = null;
    let touchAxis: 'none' | 'x' | 'y' = 'none';
    let touchStartX = 0;
    let touchStartY = 0;
    // 속도 표본: 마지막으로 적용한 스크롤 위치와 그 시각.
    let lastSampleLeft = 0;
    let lastSampleTime = 0;
    let velocity = 0;

    /** 페이징 모드(=pageWidth 를 받은 경우)의 페이지 폭. 아니면 null. */
    const getPageWidth = (): number | null => {
      const { pageWidth } = optionsRef.current;
      return pageWidth != null && pageWidth > 0 ? pageWidth : null;
    };

    /** 드래그 목표 위치를 시작 페이지 ±1 로 제한한다(pageWidth 를 받은 경우에만). */
    const clampToOnePage = (target: number): number => {
      const pageWidth = getPageWidth();
      if (pageWidth === null) return target;
      const startPage = Math.round(startScrollLeft / pageWidth);
      return Math.min(
        (startPage + 1) * pageWidth,
        Math.max((startPage - 1) * pageWidth, target),
      );
    };

    const getMaxScrollLeft = (): number =>
      Math.max(0, node.scrollWidth - node.clientWidth);

    /**
     * 손을 뗀 시점의 착지 페이지를 정한다. 속도가 임계 이상이면 그 방향으로 한 장,
     * 아니면 절반을 넘겼는지로 판정한다. 어느 경우든 시작 페이지 ±1 을 벗어나지 않는다.
     */
    const resolveReleaseTarget = (
      pageWidth: number,
      timeStamp: number,
    ): number => {
      const startPage = Math.round(startScrollLeft / pageWidth);
      const delta = node.scrollLeft - startScrollLeft;
      const isSampleFresh =
        timeStamp - lastSampleTime <= VELOCITY_SAMPLE_MAX_AGE_MS;
      const isFlick =
        isSampleFresh && Math.abs(velocity) >= FLICK_VELOCITY_THRESHOLD;
      const direction = isFlick
        ? Math.sign(velocity)
        : Math.abs(delta) > pageWidth / 2
          ? Math.sign(delta)
          : 0;
      return Math.min(
        getMaxScrollLeft(),
        Math.max(0, (startPage + direction) * pageWidth),
      );
    };

    /** 이번 프레임의 스크롤 위치를 적용하고 속도 표본을 갱신한다. */
    const applyScrollLeft = (target: number, timeStamp: number) => {
      node.scrollLeft = target;
      const elapsed = timeStamp - lastSampleTime;
      if (elapsed > 0) {
        velocity = (target - lastSampleLeft) / elapsed;
        lastSampleLeft = target;
        lastSampleTime = timeStamp;
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      isDown = true;
      moved = false;
      startX = e.pageX;
      startScrollLeft = node.scrollLeft;
      node.style.cursor = 'grabbing';
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (!moved && Math.abs(dx) < DRAG_THRESHOLD) return;
      // 임계값을 넘은 첫 순간이 "드래그 시작"이다(단순 클릭과 구분).
      if (!moved) optionsRef.current.onDragStart?.();
      moved = true;
      e.preventDefault();
      const target = clampToOnePage(startScrollLeft - dx);
      node.scrollLeft = target;
      // 검증된 RN API 로도 이동(pagingEnabled 등에서 scrollLeft 직접 조작이 무시될 때 대비).
      inst.scrollTo?.({ x: target, animated: false });
    };

    const onMouseUp = () => {
      if (isDown && moved) optionsRef.current.onDragEnd?.();
      isDown = false;
      node.style.cursor = 'grab';
    };

    // 드래그였으면 뒤따르는 click 을 capture 단계에서 차단(자식 배너 onPress 방지).
    const onClickCapture = (e: MouseEvent) => {
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
        moved = false;
      }
    };

    // ── 터치 (페이징 모드 전용) ─────────────────────────────────────────
    const findTouch = (touches: TouchList, id: number): Touch | null => {
      for (let i = 0; i < touches.length; i++) {
        if (touches[i].identifier === id) return touches[i];
      }
      return null;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (getPageWidth() === null) return;
      // 멀티터치(핀치 등)는 우리 제스처가 아니다.
      if (e.touches.length !== 1) {
        touchId = null;
        return;
      }
      const touch = e.touches[0];
      touchId = touch.identifier;
      touchAxis = 'none';
      moved = false;
      touchStartX = touch.pageX;
      touchStartY = touch.pageY;
      startScrollLeft = node.scrollLeft;
      lastSampleLeft = node.scrollLeft;
      lastSampleTime = e.timeStamp;
      velocity = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (touchId === null) return;
      const pageWidth = getPageWidth();
      if (pageWidth === null) return;
      const touch = findTouch(e.touches, touchId);
      if (!touch) return;

      const dx = touch.pageX - touchStartX;
      const dy = touch.pageY - touchStartY;

      if (touchAxis === 'none') {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_THRESHOLD) return;
        touchAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (touchAxis === 'x') {
          moved = true;
          optionsRef.current.onDragStart?.();
        }
      }
      // 세로로 확정된 제스처는 브라우저(페이지 스크롤)에 그대로 넘긴다.
      if (touchAxis !== 'x') return;

      applyScrollLeft(clampToOnePage(startScrollLeft - dx), e.timeStamp);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (touchId === null) return;
      const wasHorizontal = touchAxis === 'x';
      touchId = null;
      touchAxis = 'none';
      if (!wasHorizontal) return;

      const pageWidth = getPageWidth();
      if (pageWidth !== null) {
        const target = resolveReleaseTarget(pageWidth, e.timeStamp);
        // 스냅은 애니메이션으로. 브라우저 관성이 없으니 싸울 상대가 없다.
        inst.scrollTo?.({ x: target, animated: true });
      }
      optionsRef.current.onDragEnd?.();
    };

    // 이미지 위 드래그 시 브라우저 네이티브 이미지 드래그(고스트)를 막는다.
    const onDragStart = (e: Event) => e.preventDefault();

    // 가로 성분이 우세한 휠만 차단한다(세로는 페이지 스크롤로 흘려보낸다).
    // Chrome 의 shift+wheel 도 deltaX 로 오므로 함께 막힌다.
    const onWheel = (e: WheelEvent) => {
      if (!optionsRef.current.blockHorizontalWheel) return;
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
    };

    // ⚠️ mousedown 은 capture 단계로 잡는다. 배너 아이템(Flex onPress)이 RN Web 에서
    // mousedown 을 소비/중단해도, capture 는 자식보다 먼저 실행돼 드래그 시작을 놓치지 않는다.
    node.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    node.addEventListener('click', onClickCapture, true);
    node.addEventListener('dragstart', onDragStart);
    // preventDefault 를 하려면 passive 가 아니어야 한다(브라우저 기본은 passive).
    node.addEventListener('wheel', onWheel, { passive: false });
    // 터치는 passive 로 둔다 — `touch-action: pan-y` 가 이미 가로 팬을 막으므로
    // preventDefault 가 필요 없고, passive 가 스크롤 성능에 유리하다.
    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: true });
    node.addEventListener('touchend', onTouchEnd, { passive: true });
    node.addEventListener('touchcancel', onTouchEnd, { passive: true });
    node.style.cursor = 'grab';

    return () => {
      node.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      node.removeEventListener('click', onClickCapture, true);
      node.removeEventListener('dragstart', onDragStart);
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('touchcancel', onTouchEnd);
      node.style.cursor = '';
    };
  }, [scrollRef]);

  /**
   * 페이징 모드에서만 브라우저의 **가로** 팬을 끈다. 세로(`pan-y`)는 살려서 배너 위에서
   * 페이지 스크롤이 그대로 되게 한다. 자유 스크롤은 브라우저에 맡기므로 건드리지 않는다.
   *
   * `pageWidth` 가 0 → 실제 값으로 늦게 채워지는 소비처가 있어 리스너 부착과 분리해 둔다.
   */
  const isPaging = (options.pageWidth ?? 0) > 0;
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const inst = scrollRef.current;
    if (!inst) return;
    const node = (inst.getScrollableNode?.() ??
      findNodeHandle(
        inst as unknown as Parameters<typeof findNodeHandle>[0],
      )) as HTMLElement | null;
    if (!node) return;
    if (!isPaging) return;

    const previous = node.style.touchAction;
    node.style.touchAction = 'pan-y';
    return () => {
      node.style.touchAction = previous;
    };
  }, [scrollRef, isPaging]);
};
